import { Disclosure } from "../core/disclosure";
import { PluginRegistry } from "../core/pluginRegistry";

export class ButtonSelectDropdown extends Disclosure {
  private documentClickHandler: (event: MouseEvent) => void;
  private disableHandler: (event: CustomEvent) => void;
  private radioChangeHandlers: Map<HTMLElement, () => void> = new Map();
  private radioClickHandlers: Map<HTMLElement, (e: Event) => void> = new Map();
  private isDisabled = false;
  private selectHandler: (event: CustomEvent) => void;
  private lastChecked: HTMLInputElement | null = null;
  private defaultLabel: string = "Select";

  constructor(element: HTMLElement) {
    super(element, {
      triggerSelector: "[data-name='core-buttonSelect']",
      contentSelector: ".core-buttonSelect-dropdown",
      eventNameRaw: "buttonSelectDropdown",
    });

    this.documentClickHandler = this.handleDocumentClick.bind(this);
    this.disableHandler = (e: CustomEvent) => this.handleDisableEvent(e);
    this.selectHandler = (e: CustomEvent) => this.handleSelectEvent(e);

    document.addEventListener(
      "buttonSelectDropdown:disable",
      this.disableHandler as EventListener,
    );
  }

  init(): void {
    if (this.isDisabled) return;

    const labelElement = this.element.querySelector(
      ".core-buttonSelect__label",
    );
    if (labelElement) {
      this.defaultLabel =
        this.element.dataset.defaultLabel ||
        labelElement.textContent ||
        "Select";
    }

    this.initDisclosure();
    this.bindSpecificEvents();

    this.element.addEventListener(
      "buttonSelectDropdown:select",
      this.selectHandler as EventListener,
    );
  }

  private bindSpecificEvents(): void {
    document.addEventListener("click", this.documentClickHandler, true);

    if (this.content) {
      const radioButtons = this.content.querySelectorAll<HTMLInputElement>(
        "input[type='radio']",
      );

      const initialSelected = Array.from(radioButtons).find((r) => r.checked);
      if (initialSelected) this.lastChecked = initialSelected;

      this.updateLabel();

      radioButtons.forEach((radio) => {
        const changeHandler = () => {
          this.lastChecked = radio;
          this.updateLabel();
          this.dispatchChangeEvent();
          setTimeout(() => this.collapse(), 100);
        };

        const clickHandler = (e: Event) => {
          const target = e.target as HTMLInputElement;
          if (target === this.lastChecked) {
            target.checked = false;
            this.lastChecked = null;
            this.updateLabel();
            this.dispatchChangeEvent();
            setTimeout(() => this.collapse(), 100);
          }
        };

        this.radioChangeHandlers.set(radio, changeHandler);
        this.radioClickHandlers.set(radio, clickHandler);

        radio.addEventListener("change", changeHandler);
        radio.addEventListener("click", clickHandler);
      });
    }

    this.bindKeyboardNavigation();
  }

  private bindKeyboardNavigation(): void {
    this.trigger?.addEventListener("keydown", (e: Event) => {
      const ke = e as KeyboardEvent;
      if (ke.key === "ArrowDown" || ke.key === "ArrowUp") {
        ke.preventDefault();
        if (!this.isExpanded()) {
          this.expand();
        }
        requestAnimationFrame(() => this.focusFirstItem());
      }
      if (ke.key === "Escape" && this.isExpanded()) {
        ke.preventDefault();
        this.collapse();
      }
    });

    this.element.addEventListener("keydown", (e: Event) => {
      const ke = e as KeyboardEvent;
      if (!this.isExpanded() || !this.content) return;

      const items = this.getNavigableItems();
      if (items.length === 0) return;

      const active = document.activeElement as HTMLElement;
      const currentIndex = items.indexOf(active);

      switch (ke.key) {
        case "ArrowDown": {
          ke.preventDefault();
          const next = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
          items[next].focus();
          break;
        }
        case "ArrowUp": {
          ke.preventDefault();
          const prev = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
          items[prev].focus();
          break;
        }
        case "Home": {
          ke.preventDefault();
          items[0].focus();
          break;
        }
        case "End": {
          ke.preventDefault();
          items[items.length - 1].focus();
          break;
        }
        case "Enter":
        case " ": {
          ke.preventDefault();
          if (active) active.click();
          break;
        }
        case "Escape": {
          ke.preventDefault();
          this.collapse();
          break;
        }
        case "Tab": {
          this.collapse();
          break;
        }
      }
    });
  }

  private getNavigableItems(): HTMLElement[] {
    if (!this.content) return [];
    return Array.from(
      this.content.querySelectorAll<HTMLElement>(
        "a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex='-1'])",
      ),
    );
  }

  private focusFirstItem(): void {
    const items = this.getNavigableItems();
    if (items.length > 0) {
      items[0].focus();
    }
  }

  private handleDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!this.element.contains(target) && this.isExpanded()) {
      this.collapse();
    }
  }

  private handleDisableEvent(event: CustomEvent): void {
    const { disable, scope } = event.detail || {};

    if (scope && !this.element.closest(scope)) {
      return;
    }

    const shouldDisable = !!disable;

    if (shouldDisable === this.isDisabled) {
      return;
    }

    this.isDisabled = shouldDisable;

    if (this.isDisabled) {
      this.element.classList.add("is-disabled");
      if (this.isExpanded()) {
        this.collapse();
      }
      this.destroy();
    } else {
      this.element.classList.remove("is-disabled");
      this.trigger?.removeAttribute("disabled");
      this.init();
    }
  }

  private handleSelectEvent(event: CustomEvent): void {
    const { value, scope } = event.detail || {};

    if (scope && !this.element.closest(scope)) {
      return;
    }

    if (!this.content || !value) return;

    const radioButtons = this.content.querySelectorAll<HTMLInputElement>(
      "input[type='radio']",
    );
    const targetRadio = Array.from(radioButtons).find((r) => r.value === value);

    if (targetRadio) {
      targetRadio.checked = true;
      this.lastChecked = targetRadio;
      this.updateLabel();
      if (this.isExpanded()) {
        setTimeout(() => this.collapse(), 100);
      }
    }
  }

  private updateLabel(): void {
    if (!this.content) return;
    const labelElement = this.element.querySelector(
      ".core-buttonSelect__label",
    );
    if (!labelElement) return;

    const radioButtons = this.content.querySelectorAll<HTMLInputElement>(
      "input[type='radio']",
    );
    const selected = Array.from(radioButtons).find((r) => r.checked);

    if (selected) {
      labelElement.textContent =
        selected.ariaLabel || selected.dataset.label || selected.value;
    } else {
      labelElement.textContent = this.defaultLabel;
    }
  }

  /** Duración del cierre en ms (misma que la animación CSS: 0.5s) */
  private static readonly COLLAPSE_ANIMATION_MS = 500;

  public override collapse(): void {
    if (!this.isExpanded()) return;

    if (this.content?.contains(document.activeElement)) {
      this.trigger?.focus();
    }

    this.element.setAttribute("aria-expanded", "false");
    this.trigger?.setAttribute("aria-expanded", "false");

    setTimeout(() => {
      if (this.content) {
        this.content.setAttribute("aria-hidden", "true");
      }
      this.dispatchDisclosureEvent("collapsed");
    }, ButtonSelectDropdown.COLLAPSE_ANIMATION_MS);
  }

  private dispatchChangeEvent(): void {
    if (!this.content) return;
    const radioButtons = this.content.querySelectorAll<HTMLInputElement>(
      "input[type='radio']",
    );
    const selected = Array.from(radioButtons).find((r) => r.checked);

    const labelElement = this.element.querySelector(
      ".core-buttonSelect__label",
    );
    const defaultLabel = labelElement?.textContent || "Select";

    this.element.dispatchEvent(
      new CustomEvent("buttonSelectDropdown:change", {
        bubbles: true,
        detail: {
          selected: !!selected,
          value: selected ? selected.value : null,
          element: selected || null,
          label: selected
            ? selected.ariaLabel || selected.dataset.label || selected.value
            : defaultLabel,
        },
      }),
    );
  }

  destroy(): void {
    super.destroy();
    document.removeEventListener("click", this.documentClickHandler, true);
    this.element.removeEventListener(
      "buttonSelectDropdown:select",
      this.selectHandler as EventListener,
    );

    this.radioChangeHandlers.forEach((handler, radio) => {
      radio.removeEventListener("change", handler);
    });
    this.radioChangeHandlers.clear();

    this.radioClickHandlers.forEach((handler, radio) => {
      radio.removeEventListener("click", handler);
    });
    this.radioClickHandlers.clear();
  }
}

PluginRegistry.register("buttonSelect-dropdown", ButtonSelectDropdown);
